import React, { ReactNode, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useDebounceInput } from '@nhl/react-hooks/lib/useDebounce';

import { filterActionCreators } from '~/redux/ducks/playersAndTeams/filter';
import { ReportCategory } from '~/redux/enums';
import { commonMessages } from '~/utility/commonMessages';
import { StyledSelect } from '~/views/components/filters/playerSearch/styles';

import OptionLabel from './components/optionLabel/OptionLabel';
import getPlayers from './getPlayers';
import { PlayerOption } from './types';


const DEBOUNCE_DELAY = 300;

const PlayerSearchComponent = () => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const reportCategory = useSelector((state) => state.playersAndTeams.report.reportCategory);
    const playerSearchValue = useSelector((state) => state.playersAndTeams.filter.values.playerSearch);

    const [playerSearch, setPlayerSearch] = useState<string>(playerSearchValue);

    const [players, setPlayers] = useState<Array<PlayerOption>>(
        playerSearchValue && !playerSearchValue.value
            ? [{ value: playerSearchValue, label: playerSearchValue, key: playerSearchValue }]
            : [],
    );

    // eslint-disable-next-line no-nested-ternary
    const [selectedPlayer, setSelectedPlayer] = useState<PlayerOption>(
        playerSearch
            ? playerSearchValue.value
                ? playerSearchValue
                : { value: playerSearchValue, label: playerSearchValue, key: playerSearchValue }
            : null,
    );

    const debouncedSearch = useDebounceInput(playerSearch, DEBOUNCE_DELAY);

    const resetPlayerSearch = () => {
        setPlayers([]);
        setPlayerSearch('');
        // Set to null to ensure placeholder is shown
        setSelectedPlayer(null);
    };

    useEffect(() => {
        // Player Search is cleared
        if (!playerSearchValue && playerSearch !== playerSearchValue) {
            resetPlayerSearch();
        }
    }, [playerSearchValue]);

    useEffect(() => {
        const findPlayers = async () => {
            if (!selectedPlayer || debouncedSearch !== selectedPlayer.label) {
                const newPlayers = await getPlayers(debouncedSearch, reportCategory);
                setPlayers(newPlayers);

                if (!debouncedSearch) {
                    setSelectedPlayer({ value: null, label: '', key: null });
                }
            }
        }

        findPlayers();
    }, [debouncedSearch])

    const handleChange = (e, selectedOption: PlayerOption | null) => {
        if (!selectedOption || !selectedOption.value) {
            resetPlayerSearch();
            dispatch(filterActionCreators.setPlayerSearch(''));
        } else {
            setSelectedPlayer(selectedOption);
            setPlayerSearch(selectedOption.label);
            dispatch(filterActionCreators.setPlayerSearch(selectedOption.label));
        }
    };

    // When typing in a name to player search we fetch player names. However, we don't dispatch an action to set the
    // player state in redux. We only set the player state when an option is actually selected (handleChange)
    const handleInputChange = async (e, playerName: string) => {
        setPlayerSearch(playerName);
    };

    const getCustomOptionLabel = (option: PlayerOption): ReactNode => (
        <OptionLabel isSkater={reportCategory === ReportCategory.Skater} option={option}  />
    );

    return (
        <StyledSelect
            freeSolo
            placeholder={intl.formatMessage(commonMessages.labels.search)}
            options={players}
            selectedOption={selectedPlayer}
            inputValue={playerSearch}
            onOptionChange={handleChange}
            onInputChange={handleInputChange}
            onValueClear={(e) => handleChange(e, null)}
            dropdownButtonAriaLabel=""
            optionsBoxAriaLabel=""
            isSearchEnabled
            isClearEnabled
            getCustomOptionLabel={getCustomOptionLabel}
        />
    );
};

export default PlayerSearchComponent;
